'use strict';
import bugsnag from '@bugsnag/js';
import bugsnagVue from '@bugsnag/plugin-vue';
import axios from 'axios';
import { msedge, msie, version } from 'bowser';
import cookies from 'js-cookie';
import LazySizes from 'lazysizes';
// tslint-disable-next-line
import LSUnveilHoks from 'lazysizes/plugins/unveilhooks/ls.unveilhooks';
import get from 'lodash/get';
import isString from 'lodash/isString';
import set from 'lodash/set';
import throttle from 'lodash/throttle';
import moment from 'moment';
import smoothscroll from 'smoothscroll-polyfill';
import sbJs from 'sourcebuster';
import VCalendar from 'v-calendar';
import VueTooltip from 'v-tooltip';
import VModal from 'vue-js-modal';
import { Vue } from 'vue-property-decorator';
import VueTelInput from 'vue-tel-input';
import VueTouchEvents from 'vue2-touch-events';
import Formatter from './base/Formatter';
import { GlobalSettings } from './base/GlobalSettings';
import { africaOptions, dataLayer, destinationData, page, toursData, tourData } from './globals';
import EventBusPlugin from './helpers/bus';
import { Gdpr } from './helpers/gdpr';
import { withInterceptors } from './helpers/interceptors';
import { trackTour } from './helpers/lastSeenTours';
import { urlParam } from './helpers/utils';
import VueScroll from './vendor/vuescroll';

// if ('serviceWorker' in window.navigator) {
//     window.navigator.serviceWorker
//       .register('/sw.js');
// }

if ('serviceWorker' in window.navigator) {
    window.navigator.serviceWorker.getRegistrations().then(function(registrations) {
        for (const registration of registrations) {
            registration.unregister();
        }
    });
}

if (msie || msedge) {
    document.body.classList.add('is-ie');
}
if (msie && version <= 10) {
    document.body.classList.add('is-outdated-browser');
}

window.bugsnagClient = bugsnag({
    apiKey: 'c55f35bc3889963f897424848d115f04',
    notifyReleaseStages: ['prod', 'test'],
    appVersion: window.africaOptions.instance.build,
    appType: 'client',
    releaseStage: window.africaOptions.instance.stage,
});
window.bugsnagClient.use(bugsnagVue, Vue);

smoothscroll.polyfill();

Vue.config.productionTip = false;
Vue.use(EventBusPlugin);
Vue.use(VCalendar, {
    locale: get({
        de: 'de',
        dk: 'da',
        uk: 'en-gb',
        se: 'sv',
        no: 'nb',
        nl: 'nl',
        fi: 'fi',
        fr: 'fr',
    }, africaOptions.instance.locale, 'en-gb'),
    firstDayOfWeek: 2,
    formats: {
        title: 'MMMM YYYY',
        weekdays: 'WWW',
        navMonths: 'MMM',
        input: ['L', 'YYYY-MM-DD', 'YYYY/MM/DD'],
        dayPopover: 'L',
        data: ['L', 'YYYY-MM-DD', 'YYYY/MM/DD'],
    },
});
Vue.use(VModal, { dynamic: true, injectModalsContainer: true });
Vue.use(VueScroll);
Vue.use(VueTouchEvents);
Vue.use(VueTooltip, {
    defaultTemplate: '<div class="c-tooltip" role="tooltip"><div class="c-tooltip__arrow"></div><div class="c-tooltip__inner"></div></div>',
    defaultArrowSelector: '.c-tooltip__arrow',
    defaultInnerSelector: '.c-tooltip__inner',
    defaultLoadingClass: 'c-tooltip--loading',
    popover: {
        defaultBaseClass: 'c-tooltip c-popover',
        defaultWrapperClass: 'c-tooltip__wrapper',
        defaultInnerClass: 'c-tooltip__inner c-popover__inner',
        defaultArrowClass: 'c-tooltip__arrow c-popover__arrow',
    },
});

const countries = {
    de: ['DE', 'AT', 'CH', 'LU', 'NL', 'BE', 'GB', 'FR'],
    dk: ['DK', 'SE', 'NO', 'IS', 'FO', 'GL', 'DE'],
    uk: ['GB', 'IE', 'FR', 'ES', 'DE', 'US'],
    se: ['SE', 'NO', 'DK', 'IS', 'DE', 'GB'],
    no: ['NO', 'SE', 'DK', 'IS', 'DE', 'GB'],
    nl: ['NL', 'BE', 'DE', 'FR', 'UK'],
    fi: ['FI', 'SE', 'NO'],
    fr: ['FR', 'CH', 'BE', 'LU', 'ES', 'IT', 'DE', 'GB'],
};

Vue.use(VueTelInput, {
    defaultCountry: [...get(countries, africaOptions.instance.locale, ['GB'])].shift(),
    onlyCountries: get(countries, africaOptions.instance.locale, Array.from(new Set(Object.values(countries).flat().sort()))),
});

const lazySizesConfig = window.lazySizesConfig || {};

lazySizesConfig.loadMode = 1;
lazySizesConfig.loadHidden = false;
lazySizesConfig.lazyClass = 'u-lazy';
lazySizesConfig.preloadClass = 'u-lazy-preload';
lazySizesConfig.loadedClass = 'u-lazy-loaded';

// Init image lazy-loading lib.
// This script must be loaded as fast as possible and
// should be placed before any other blocking elements (i.e. scripts)
// at the end of the body or after all of the blocking elements in the head.
// picturefill();

// Support for the lazy-loading of the background images
document.addEventListener('lazybeforeunveil', (e: Event) => {
    if (e.target instanceof HTMLElement) {
        const bgImage = e.target.getAttribute('data-bg');
        if (bgImage) {
            e.target.style.backgroundImage = `url(${bgImage})`;
        }
    }
});

LazySizes.init();

require('moment-timezone');
require('moment/locale/de');
require('moment/locale/da');
require('moment/locale/en-gb');
require('moment/locale/sv');
require('moment/locale/nb');
require('moment/locale/nl');
require('moment/locale/fi');
require('moment/locale/fr');

const globalSettings = new GlobalSettings(africaOptions);

const gdpr = new Gdpr();

const formatter = new Formatter(africaOptions.formatter || {});

const metas: NodeListOf<HTMLMetaElement> = document.getElementsByTagName('meta');

const getMeta = (name: string): HTMLMetaElement | undefined => {
    const meta: any = get(metas, name);
    if (meta instanceof HTMLMetaElement) {
        return meta;
    }
};

const headers = {};
const header = getMeta('csrf-header');
const token = getMeta('csrf-token');

if (header && token) {
    set(headers, header.content, token.content);
}

// Add interceptors:
// - when request is failed then show a modal window with an error message
const $http = withInterceptors(axios.create({
    baseURL: africaOptions.api.baseUrl,
    headers,
}));

moment.locale(get({
    de: 'de',
    dk: 'da',
    uk: 'en-gb',
    se: 'sv',
    no: 'nb',
    nl: 'nl',
    fi: 'fi',
    fr: 'fr',
}, africaOptions.instance.locale, 'en-gb'));

/**
 * Prepare data layer with base data for GTM
 *
 * TODO: I think we could remove global a 'toursData var. And generate on each page JS block with needed base information.
 * I think so because our app has not a global state.
 */
dataLayer.push({ pageType: page.type });
if (africaOptions.page.controller === 'page' && africaOptions.page.action === 'home') {
    dataLayer.push({ travel_pagetype: 'home' });
} else if (['location', 'destination', 'landing-page', 'practical-info'].indexOf(africaOptions.page.controller) !== -1) {
    dataLayer.push({
        travel_pagetype: 'searchresults',
        travel_destid: africaOptions.page.hash,
    });

    // TODO: I think this block do nothing. I didn't find where the 'destinationData' is using
    if (africaOptions.page.controller === 'destination') {
        let des: any;
        const tours: string[] = [];
        toursData.forEach((tour) => {
            if (tour.destination.id === africaOptions.page.id) {
                des = tour.destination;
                tours.push(tour.hash);
            }
        });
        if (des) {
            destinationData.name = des.name;
            destinationData.tourIds = tours;
        }
    }
} else if (africaOptions.page.controller === 'tour') {
    // document.addEventListener('DOMContentLoaded', (event) => {
    //     const start = window.location.href.indexOf('/');
    //     if (start > -1) {
    //         const asBaseName = window.location.href.substr(start + '/tours/'.length);
    //         window.caches.open(asBaseName).then(function(cache) {
    //             const urls = Array.from(document.querySelectorAll('[data-bg]')).map((elem) => {
    //                 if (elem instanceof HTMLElement) {
    //                     return elem.dataset.bg;
    //                 }
    //                 return null;
    //             }).filter((url) => !!url);
    //             cache.addAll(urls);
    //         });
    //     }
    // });

    dataLayer.push({
        travel_pagetype: 'offerdetail',
        travel_destid: africaOptions.page.hash,
    });
    let tour: any;
    toursData.forEach((t) => {
        if (t.id === africaOptions.page.id) {
            tour = t;
        }
    });
    // TODO: I don't know why we hidrate a tourData because this global var is don't use anywhere
    if (tour) {
        tourData.id = tour.hash;
        tourData.name = tour.name;
        tourData.brand = tour.brand;
        tourData.brandLabel = tour.brandLabel;
        tourData.category = tour.destination.name;
        trackTour(tour.id);
    }
} else if (window.location.href.match(new RegExp('\/request\/success-' + africaOptions.instance.locale + '\.htm')) !== null && urlParam('tid', false)) {
    const tId = urlParam('tid', false);
    const date = urlParam('time', false);
    const tourId = parseInt(urlParam('selectedTour', 0), 10);
    dataLayer.push({
        originDate: date,
    });
    let tour: any;
    toursData.forEach((t) => {
        if (t.id === tourId) {
            tour = t;
        }
    });
    if (tour) {
        dataLayer.push({
            travel_pagetype: 'conversion',
            travel_destid: tour.hash,
        });
        tourData.id = tour.hash;
        tourData.name = tour.name;
        tourData.brand = tour.brand;
        tourData.brandLabel = tour.brandLabel;
        tourData.category = tour.destination.name;
        dataLayer.push({
            ecommerce: {
                purchase: {
                    actionField: {
                        id: tId,
                        affiliation: 'Aclass Store',
                        revenue: 0,
                        tax: 0,
                        shipping: 0,
                        coupon: '',
                    },
                    products: [{
                        name: tourData.name,
                        id: tourData.id,
                        price: 0,
                        brand: tourData.brand,
                        category: tourData.category,
                        variant: '',
                        quantity: 1,
                        coupon: '',
                    }],
                },
            },
        });
    }
}
// End

// Scroll to the end of the page event
function scrollHandler(listener) {
    if ((window.innerHeight + window.pageYOffset) >= document.body.offsetHeight) {
        window.removeEventListener('scroll', listener);
        dataLayer.push({ event: 'page:hitBottom' });
    }
}
const sh = throttle(scrollHandler, 100, { leading: false });

window.removeEventListener('scroll', () => sh(sh));
window.addEventListener('scroll', () => sh(sh), { passive: true });
// End

sbJs.init({
    callback(sb: any) {
        const session = cookies.get('sbjs_session');
        if (!isString(session)) {
            return;
        }
        const delimiter = '|||';
        let isStartSet = false;
        session.split(delimiter).forEach((data) => {
            if (get(data.split('='), 0) === 'start') {
                isStartSet = true;
            }
        });
        if (isStartSet) {
            return;
        }
        const expireTime = new Date();
        // Set expire time 30 minutes
        expireTime.setMinutes(expireTime.getMinutes() + 30);
        sb.session.start = moment().unix();
        // set new value
        cookies.set('sbjs_session', session + delimiter + 'start=' + sb.session.start, { expires: expireTime, path: '/' });
    },
});

export default class AppConfig {
    public static init(): void {
        //
        window.TrustpilotLoaded = false;
    }
}

export { globalSettings, $http, moment, formatter, sbJs, gdpr };



// WEBPACK FOOTER //
// ./src/app.config.ts